import { C, FONT } from './theme'
import { useSnapshot } from './data/source'
import { usePersistedState } from './lib/persistedState'
import { PROBE_SIZES } from './data/mock/constants'
import { Header } from './components/Header'
import { Position } from './components/Position'
import { Lending } from './components/Lending'
import { Acceptance } from './components/Acceptance'
import { PriceChart } from './components/PriceChart'
import { Today } from './components/Today'
import { Directions } from './components/Directions'
import { Feeds, type FeedKind } from './components/Feeds'
import { VolumeChart } from './components/VolumeChart'
import type { Market, Solver, WindowKey } from './types'

export default function App() {
  const snap = useSnapshot()

  const [market, setMarket] = usePersistedState<Market>('market', 'ETH-USDC')
  const [size, setSize] = usePersistedState<number>('size', 1)
  const [win, setWin] = usePersistedState<WindowKey>('acceptanceWindow', '90s')
  const [depthOpen, setDepthOpen] = usePersistedState('depthOpen', true)
  const [pnlOpen, setPnlOpen] = usePersistedState('pnlOpen', false)
  const [dirOpen, setDirOpen] = usePersistedState<string | null>('dirOpen', null)
  const [feed, setFeed] = usePersistedState<FeedKind>('feed', 'quotes')
  const [solverFilter, setSolverFilter] = usePersistedState<Solver | null>('solverFilter', null)
  const [showNotes, setShowNotes] = usePersistedState('showNotes', false)

  if (!snap) return null

  return (
    <div
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        background: C.bg,
        color: C.text,
        fontFamily: FONT,
        fontVariantNumeric: 'tabular-nums',
        padding: '26px 34px 44px',
      }}
    >
      <Header snap={snap} />

      <Position snap={snap} />
      <Lending snap={snap} />

      <div
        data-split
        style={{
          display: 'grid',
          gridTemplateColumns: '1.35fr 1fr',
          gap: 40,
          marginTop: 34,
        }}
      >
        <PriceChart
          snap={snap}
          market={market}
          onMarket={(m) => {
            setMarket(m)
            setSize(PROBE_SIZES[m][Math.floor(PROBE_SIZES[m].length / 2)])
          }}
          size={size}
          onSize={setSize}
          depthOpen={depthOpen}
          onToggleDepth={() => setDepthOpen((v) => !v)}
          showNotes={showNotes}
        />
        <Today
          snap={snap}
          open={pnlOpen}
          onToggle={() => setPnlOpen((v) => !v)}
          showNotes={showNotes}
        />
      </div>

      <Directions snap={snap} openKey={dirOpen} onOpen={setDirOpen} showNotes={showNotes} />

      <div style={{ marginTop: 34 }}>
        <Acceptance snap={snap} win={win} onWin={setWin} showNotes={showNotes} />
      </div>

      <Feeds
        snap={snap}
        feed={feed}
        onFeed={setFeed}
        solverFilter={solverFilter}
        onSolverFilter={setSolverFilter}
        showNotes={showNotes}
      />

      <VolumeChart />

      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          rowGap: 6,
          borderTop: `1px solid ${C.border}`,
          marginTop: 40,
          paddingTop: 14,
          fontSize: 13,
          color: C.muted,
        }}
      >
        <span>
          run {snap.runId} · host {snap.host} ·{' '}
          {import.meta.env.VITE_LIVE_SNAPSHOT_URL
            ? import.meta.env.VITE_LIVE_CHART_BASE_URL
              ? 'live mm-v2 telemetry · read-only mirror'
              : 'live mm-v2 telemetry · price chart still simulated · read-only mirror'
            : 'mock data, read-only mirror'}
        </span>
        <span
          onClick={() => setShowNotes((v) => !v)}
          style={{ cursor: 'pointer', color: C.accent, userSelect: 'none' }}
        >
          {showNotes ? 'hide the footnotes' : 'show the footnotes'}
        </span>
      </div>
    </div>
  )
}
